/* eslint-disable no-unused-vars */
import { useState } from "react";
import clsx from "clsx";
import {
  isFinalValidInputQuoteSize,
  isValidInputQuoteSize,
  returnQuoteSizeWithMConvertedToThousands,
} from "@/lib/businessRules/quoteSizeRules";
import { returnRoundedSize } from "@/lib/businessRules/minimumSizeRule";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import { formatNumberWithCommas } from "@/lib/utilities";

// LQC-2254 - nominal entry with the app's M / MM shorthand: one m is thousands, two is
// millions, so "10MM" commits 10000000. Reuses the quote-size rules the AG Grid size
// editors use, which is why the keystroke and commit validation differ - the keystroke
// regex has to allow half-typed values like "123.".
//
// While focused the raw number is shown so it can be edited; blurred it shows the app's
// abbreviation ("10 MM"). Copy always yields the raw number, matching the copy/paste
// convention of the size cells.
interface INominalInput {
  value: number | null;
  onCommit: (value: number | null) => void;
  testId: string;
  isDisabled?: boolean;
  // LQC-2949 - rounds a commit to this step and says so. Re-entering the same unrounded
  // whole number straight after keeps it, which is the override (LEG_CREATION_PLAN Q4).
  roundingStep?: number;
  // Controlled rounding state, for a cell AG Grid may remount. The caller then shows the note.
  roundedFrom?: number | null;
  onRoundedFromChange?: (roundedFrom: number | null) => void;
}

// Only while the value is still the one we rounded to, so a Clear drops the note.
export function isRoundedNoteShowing(
  value: number | null,
  roundedFrom: number | null,
  roundingStep: number | undefined,
): boolean {
  return (
    roundedFrom !== null &&
    !!roundingStep &&
    value === returnRoundedSize(roundedFrom, roundingStep)
  );
}

// `subject` names the field where the note sits away from it ("Nominal rounded from ...").
export function returnRoundedNoteText(roundedFrom: number, subject?: string): string {
  const lead = subject ? `${subject} rounded` : "Rounded";
  const hint = Number.isInteger(roundedFrom)
    ? "Re-enter it to keep it."
    : "Only a whole number can be kept.";
  return `${lead} from ${formatNumberWithCommas(roundedFrom)}. ${hint}`;
}

// The committed value, and what it was rounded from (null when not rounded).
export function returnNominalCommit(
  committed: number,
  roundingStep: number | undefined,
  lastRoundedFrom: number | null,
): { value: number; roundedFrom: number | null } {
  if (!roundingStep) return { value: committed, roundedFrom: null };
  // "1.005m" parses to 1004.9999999999999; snap that noise so it still counts as whole.
  const whole = Math.round(committed);
  const value = Math.abs(committed - whole) < 1e-6 ? whole : committed;
  const rounded = returnRoundedSize(value, roundingStep);
  // The spec's override is to a whole number, so a fraction always rounds.
  const isOverride = value === lastRoundedFrom && Number.isInteger(value);
  if (rounded === value || isOverride) {
    return { value, roundedFrom: null };
  }
  return { value: rounded, roundedFrom: value };
}

const NominalInput = ({
  value,
  onCommit,
  testId,
  isDisabled = false,
  roundingStep,
  roundedFrom: controlledRoundedFrom,
  onRoundedFromChange,
}: INominalInput) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [localRoundedFrom, setLocalRoundedFrom] = useState<number | null>(null);
  const isControlled = !!onRoundedFromChange;
  const roundedFrom = isControlled ? (controlledRoundedFrom ?? null) : localRoundedFrom;
  const setRoundedFrom = onRoundedFromChange ?? setLocalRoundedFrom;
  const isShowingRoundedNote =
    !isControlled && isRoundedNoteShowing(value, roundedFrom, roundingStep);

  const displayValue = isEditing
    ? localValue
    : value !== null
      ? returnSizeFormatted(value)
      : "";

  const handleFocus = () => {
    setLocalValue(value !== null ? String(value) : "");
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue.trim() === "") {
      onCommit(null);
      return;
    }
    // An unfinishable entry ("123." or a bare "m") is discarded, not guessed at.
    if (!isFinalValidInputQuoteSize(localValue)) return;
    const committed = returnQuoteSizeWithMConvertedToThousands(localValue);
    if (isNaN(committed)) {
      onCommit(null);
      return;
    }
    const result = returnNominalCommit(committed, roundingStep, roundedFrom);
    setRoundedFrom(result.roundedFrom);
    onCommit(result.value);
  };

  return (
    <div className="flex w-full flex-col">
      <div
        className={clsx(
          "flex h-[28px] w-full items-center rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800",
          isDisabled && "opacity-50",
        )}
      >
        <input
          data-testid={testId}
          id={testId}
          disabled={isDisabled}
          type="text"
          inputMode="decimal"
          placeholder="e.g. 10MM"
          className="h-full w-full bg-transparent px-2 text-xs outline-none dark:text-gray-200"
          value={displayValue}
          onFocus={handleFocus}
          onChange={(e) => {
            if (isValidInputQuoteSize(e.target.value)) {
              setLocalValue(e.target.value);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          onCopy={(e) => {
            if (value === null) return;
            e.preventDefault();
            e.clipboardData.setData("text/plain", String(value));
          }}
        />
      </div>
      {isShowingRoundedNote && (
        <div
          data-testid={`${testId}RoundedNote`}
          id={`${testId}RoundedNote`}
          className="px-1 pt-0.5 text-xs text-amber-600 dark:text-amber-400"
        >
          {returnRoundedNoteText(roundedFrom as number)}
        </div>
      )}
    </div>
  );
};

export default NominalInput;
